import { useState } from "react";
import { Modal, Pressable, Text, View } from "react-native";
import { haptic, IconButton } from "@louise/moon-ui";
import {
  useLocale,
  useTranslations,
  SUPPORTED_LOCALES,
  type Locale,
} from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const FLAGS: Record<Locale, string> = {
  en: "\u{1F1FA}\u{1F1F8}",
  fr: "\u{1F1EB}\u{1F1F7}",
};

const LABEL_KEYS: Record<Locale, string> = {
  en: "language_switcher.english",
  fr: "language_switcher.french",
};

export default function LanguageButton() {
  const capturePress = usePressCapture("LanguageButton");
  const { locale, setLocale } = useLocale();
  const t = useTranslations();
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        onPress={() => {
          haptic("light");
          setOpen(true);
        }}
        hitSlop={8}
        size={36}
        accessibilityLabel={t("language_switcher.language")}
      >
        <Text style={{ fontSize: 20 }}>{FLAGS[locale]}</Text>
      </IconButton>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          onPress={() => setOpen(false)}
          // Backdrop dims the page so the popover reads as one. Without
          // it (and without a width cap on the inner panel) the dropdown
          // stretched the full viewport on desktop/tablet — the
          // "language selector taking the whole screen" UAT bug (#103).
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingHorizontal: 24,
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          }}
        >
          <View
            // Stop event from bubbling so a tap inside the popover
            // doesn't dismiss it via the backdrop Pressable above.
            onStartShouldSetResponder={() => true}
            style={{
              width: "100%",
              maxWidth: 280,
              borderRadius: 12,
              backgroundColor: "rgba(255, 255, 255, 0.97)",
              overflow: "hidden",
              paddingVertical: 6,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: EMBER.colors.textLight,
                paddingHorizontal: 16,
                paddingVertical: 12,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("language_switcher.language")}
            </Text>
            {SUPPORTED_LOCALES.map((lng) => {
              const selected = lng === locale;
              return (
                <Pressable
                  key={lng}
                  onPress={() => {
                    capturePress(pressLabel(t(LABEL_KEYS[lng])), "set_locale");
                    haptic("selection");
                    void setLocale(lng);
                    setOpen(false);
                  }}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    paddingHorizontal: 16,
                    paddingVertical: 14,
                    backgroundColor: selected
                      ? "rgba(42, 37, 34, 0.05)"
                      : "transparent",
                  }}
                >
                  <Text style={{ fontSize: 20 }}>{FLAGS[lng]}</Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: selected ? "600" : "400",
                      color: selected
                        ? EMBER.colors.text
                        : EMBER.colors.textMid,
                    }}
                  >
                    {t(LABEL_KEYS[lng])}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </>
  );
}
